import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { IconExternalLink } from '@tabler/icons-react'
import { useAppStore } from '../store'
import Controls from './Controls'

const navBtnStyle: React.CSSProperties = {
  width: 29,
  height: 29,
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: 4,
  cursor: 'pointer',
  lineHeight: 1,
  padding: 0,
  boxShadow: '0 0 0 2px rgba(0,0,0,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const RAIL_WIDTH = 290
const COLLAPSED_STRIP = 4
const COLLAPSE_THRESHOLD = 60
const MIN_OPEN_WIDTH = 160

const RightRail = ({ collectionMode = false }: { collectionMode?: boolean }) => {
  const railRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(RAIL_WIDTH)
  const [dragging, setDragging] = useState(false)
  const [hovered, setHovered] = useState(false)
  const datasetModule = useAppStore((s) => s.getDatasetModule())
  const datasetState = useAppStore((s) => s.datasetState)
  const setActiveDatasetState = useAppStore((s) => s.setActiveDatasetState)
  const setRightRailWidth = useAppStore((s) => s.setRightRailWidth)
  const scope = useAppStore((s) => s.scope)
  const mapInstance = useAppStore((s) => s.mapInstance)
  const globeProjection = useAppStore((s) => s.globeProjection)
  const setGlobeProjection = useAppStore((s) => s.setGlobeProjection)
  const timeCount = useAppStore((s) => s.timeCount)
  const formatTimeIndex = useAppStore((s) => s.formatTimeIndex)
  const location = useLocation()

  const collapsed = width <= COLLAPSED_STRIP

  useEffect(() => {
    setRightRailWidth(width)
    return () => setRightRailWidth(0)
  }, [width, setRightRailWidth])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = collapsed ? 0 : width
    setDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const onMove = (ev: MouseEvent) => {
      setWidth(Math.max(0, startWidth + (startX - ev.clientX)))
    }

    const onUp = (ev: MouseEvent) => {
      const newWidth = Math.max(0, startWidth + (startX - ev.clientX))
      setWidth(newWidth < COLLAPSE_THRESHOLD ? COLLAPSED_STRIP : Math.max(MIN_OPEN_WIDTH, newWidth))
      setDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const ActiveDatasetControls = datasetModule.Controls

  return (
    <>
      {/* Zoom + projection buttons — same right offset as rail, so they move in sync */}
      <div
        style={{
          position: 'absolute',
          top: 45,
          right: width + 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          zIndex: 100,
        }}
      >
        <button
          onClick={() => (mapInstance as any)?.zoomIn()}
          title="Zoom in"
          style={{ ...navBtnStyle, fontSize: 20, fontWeight: 400, color: 'var(--navy)' }}
        >
          +
        </button>
        <button
          onClick={() => (mapInstance as any)?.zoomOut()}
          title="Zoom out"
          style={{ ...navBtnStyle, fontSize: 20, fontWeight: 400, color: 'var(--navy)' }}
        >
          −
        </button>
        <button
          onClick={() => setGlobeProjection(!globeProjection)}
          title={globeProjection ? 'Switch to 2D' : 'Switch to Globe'}
          style={{ ...navBtnStyle, fontSize: 11, fontWeight: 600, color: 'var(--teal)', marginTop: 4 }}
        >
          {globeProjection ? '2D' : '3D'}
        </button>
      </div>

      {/* Drag handle — centered on the rail's left border */}
      <div
        onMouseDown={handleMouseDown}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position: 'absolute',
          top: 0,
          right: width - 3,
          bottom: 0,
          width: 6,
          cursor: 'col-resize',
          zIndex: 1100,
          background: (hovered || dragging) ? 'rgba(0,0,0,0.06)' : 'transparent',
          transition: dragging ? 'none' : 'background 0.15s',
        }}
      />

      <div
        ref={railRef}
        className="ze-sidebar"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width,
          background: 'var(--off)',
          borderLeft: `${collapsed ? 2 : 1}px solid var(--rule)`,
          overflowY: collapsed ? 'hidden' : 'auto',
          zIndex: 900,
          padding: collapsed ? 0 : 'var(--ze-section-pad) var(--ze-section-pad) 24px',
          boxSizing: 'border-box',
          transition: dragging ? 'none' : 'width 0.15s ease',
        }}
      >
        {!collapsed && (
          <>
            {/* Header: title / subtitle / date range */}
            <div style={{ marginBottom: 6 }}>
              <div style={{
                fontFamily: 'var(--card-sans)',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--navy)',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {datasetModule.info}
              </div>
              {datasetModule.subtitle && (
                <div style={{
                  fontFamily: 'var(--card-mono)',
                  fontSize: 10,
                  color: 'var(--grey)',
                  marginTop: 2,
                }}>
                  {datasetModule.subtitle}
                </div>
              )}
              {timeCount > 1 && formatTimeIndex && (
                <div style={{
                  fontFamily: 'var(--card-mono)',
                  fontSize: 10.5,
                  color: 'var(--slate)',
                  marginTop: 3,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {formatTimeIndex(0)} → {formatTimeIndex(timeCount - 1)}
                </div>
              )}
            </div>

            <div className="ze-divider" style={{ marginTop: 8 }} />

            <div style={{ marginTop: 4 }}>
              <ActiveDatasetControls
                state={datasetState}
                setState={setActiveDatasetState}
              />
            </div>

            <div className="ze-divider" />

            <Controls />

            {(collectionMode || datasetModule.collectionId) && (
              <>
                <div className="ze-divider" />
                <a
                  href={collectionMode
                    ? location.pathname + '#overview'
                    : `/dataset/${datasetModule.collectionId}#overview`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: '0.75rem',
                    color: 'var(--teal)',
                    textDecoration: 'none',
                    marginTop: 4,
                  }}
                >
                  Collection metadata
                  <IconExternalLink size={13} stroke={2} />
                </a>
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default RightRail
export { RAIL_WIDTH as RIGHT_RAIL_WIDTH }
